import React, { useEffect, useMemo, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import LeaseApiService from '../../services/apiService';
import { isAgentApiSuccess, unwrapAgentError, resolveBrokerId, pickAgentField, toAgentBool } from './agentMasterApiHelpers';
import AgentMasterForm from './agentMasterForm';
import DocumentExpiryModal from './documentExpiryModal';
import RowActions from '../shared/rowActions';
import RangeChips from '../shared/rangeChips';
import { filterByRange, useCardFilter, valueTiles } from '../shared/rangedList';
import SummaryStrip, { countBy } from '../shared/summaryStrip';
import '../customerMaster/customerListing.css';
import { FiPlus, FiRotateCcw, FiSearch } from 'react-icons/fi';

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const AgentMasterListing = () => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const [allRows, setRowData] = useState([]);
  const [range, setRange] = useState('all');
  const [loading, setLoading] = useState(true);
  // The agent list returns every record, so the period (by created date, when present) is applied here.
  const rowData = useMemo(() => filterByRange(allRows, range), [allRows, range]);
  const cf = useCardFilter(rowData, 'all', null);
  const typeOf = (r) => pickAgentField(r, 'type', 'Type');
  const activeOf = (r) => toAgentBool(r?.active);
  const [FilterTab, setFilterTab] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const [showDocExpiry, setShowDocExpiry] = useState(false);

  useEffect(() => {
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // getlist has no pagination (confirmed live: returns the full matching array),
  // so search is a single "Name" filter matching the real request body, and
  // everything else is filtered client-side via the grid's own floating filters.
  const getItems = async (name = nameFilter) => {
    try {
      setLoading(true);
      const res = await apiService.post('AGENT_MASTER_LIST', { name: name || '' });
      if (isAgentApiSuccess(res?.data)) {
        setRowData(res?.data?.data || []);
      } else {
        setRowData([]);
        toast.warning(unwrapAgentError(res?.data));
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error(t('agentMaster.loadFailed'));
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setNameFilter('');
    getItems('');
  };

  const handleNew = () => setFormModal({ mode: 'create', data: null });

  const handleView = (row) => {
    if (!resolveBrokerId(row)) {
      toast.warning(t('agentMaster.missingRecordIdView'));
    }
    setFormModal({ mode: 'view', data: row });
  };

  const handleEdit = (row) => {
    if (!resolveBrokerId(row)) {
      toast.warning(t('agentMaster.missingRecordIdEdit'));
    }
    setFormModal({ mode: 'edit', data: row });
  };

  const closeFormModal = () => setFormModal({ mode: null, data: null });

  const handleFormSaved = () => {
    closeFormModal();
    getItems();
  };

  const columnDefs = [
    {
      headerName: t('common.actions'),
      pinned: 'left',
      width: 110,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params) => <RowActions data={params.data} onView={handleView} onEdit={handleEdit} />,
    },
    {
      headerName: t('agentMaster.columns.agentId'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      valueGetter: (p) => resolveBrokerId(p.data),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.type'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 120,
      valueGetter: (p) => pickAgentField(p.data, 'type', 'Type'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.name'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (p) => pickAgentField(p.data, 'broker_Name', 'name'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.contactPerson'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => pickAgentField(p.data, 'contactPerson'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.phoneNo'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 140,
      valueGetter: (p) => pickAgentField(p.data, 'brokerLandLineNo', 'landlineNo'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.mobileNo'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 140,
      valueGetter: (p) => pickAgentField(p.data, 'brokerMobileNo', 'mobileNo'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.emailId'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (p) => pickAgentField(p.data, 'brokerEmailID', 'email'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.active'),
      suppressMovable: true,
      sortable: true,
      width: 100,
      valueGetter: (p) => (toAgentBool(p.data?.active) ? t('common.yes') : t('common.no')),
    },
  ];

  return (
    <div className="agent-list-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
        <h1>{t('agentMaster.pageTitle')}</h1>
        <p>Agents and brokers who bring customers and deals, with their contact details and documents.</p>
      </div>
        <RangeChips value={range} onChange={setRange} />
      </div>
      <SummaryStrip
        label="Total agents"
        total={rowData.length}
        subtitle={`${rowData.filter(activeOf).length} active`}
        heroNote={range === 'all' ? 'All records' : 'Chosen period'}
        heroActive={!cf.filter}
        onHero={() => cf.setFilter(null)}
        tiles={[
          { label: 'Active', value: rowData.filter(activeOf).length, color: '#1E7A4F', note: 'agents', active: cf.filter?.id === 'active', onClick: () => cf.toggle({ id: 'active', test: activeOf }) },
          { label: 'Inactive', value: rowData.length - rowData.filter(activeOf).length, color: '#B3261E', note: 'agents', active: cf.filter?.id === 'inactive', onClick: () => cf.toggle({ id: 'inactive', test: (r) => !activeOf(r) }) },
          ...valueTiles(cf, 'type', 3, { get: typeOf, note: 'agents' }),
        ]}
        ariaLabel="Agent summary"
      />
      <section className="cl-panel cl-filters" aria-label={t('common.searchParameters')}>
        <div>
          <div>
            <div className="d-flex gap-3 align-items-stretch">
              <div className="form-group grid-contain flex-grow-1">
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('agentMaster.filters.name')}</label>
                  <input
                    type="text"
                    className="p-2 form-control w-75"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="cl-filter-actions-inline">
                <button type="button" className="cl-btn cl-btn-ghost" onClick={handleClearFilters}><FiRotateCcw size={16} />{t('common.reset')}</button>
                <button type="button" className="cl-btn cl-btn-primary" onClick={() => getItems()}><FiSearch size={16} />{t('common.search')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cl-panel cl-list">
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="cl-list-head">
              <h2>{t('agentMaster.pageTitle')}</h2>
              <div className="cl-head-actions">
                <button type="button" className="cl-btn cl-btn-ghost cl-btn-sm" onClick={() => setShowDocExpiry(true)}>{t('agentMaster.documentExpiredList')}</button>
                <button type="button" className="cl-btn cl-btn-primary cl-btn-sm" onClick={handleNew}><FiPlus size={16} />{t('common.add')}</button>
              </div>
            </div>
            <div className="cl-grid-wrap">
              <div className="ag-theme-quartz customer-grid" style={{ height: 650 }}>
                <AgGridReact
                  rowData={cf.shownRows}
                  columnDefs={columnDefs}
                />
              </div>
            </div>
          </>
        )}
      </section>

      <Modal
        show={!!formModal.mode}
        onHide={closeFormModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="customer-form-modal"
        contentClassName="p-0"
      >
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode && <AgentMasterForm mode={formModal.mode} initialData={formModal.data} onClose={closeFormModal} onSaved={handleFormSaved} />}
        </Modal.Body>
      </Modal>

      <DocumentExpiryModal show={showDocExpiry} onClose={() => setShowDocExpiry(false)} />
    </div>
  );
};

export default AgentMasterListing;
